import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ActivityCalendar from '../components/Calendar/ActivityCalendar';
import { CalendarIcon, ListIcon, PlusIcon } from '../components/common/Icons';
import useIsMobile from '../hooks/useIsMobile';
import useGlobalView from '../hooks/useGlobalView';
import ChurchBadge from '../components/common/ChurchBadge';
import usePermissions from '../hooks/usePermissions';
import { checkRequired, requiredMessage, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const ACTIVITY_LABELS = { name: 'Nom', date: 'Date', start_time: 'Heure de début', end_time: 'Heure de fin', end_date: 'Date de fin', activity_type: "Type d'activité", location: 'Lieu', recurrence_days: 'Jours de répétition', recurrence_end: 'Fin de la récurrence' };

const STATUS_LABELS = { planned: 'Planifiée', ongoing: 'En cours', completed: 'Terminée', cancelled: 'Annulée' };
const STATUS_COLORS = {
  planned:   { bg: '#eff6ff', color: '#1e40af' },
  ongoing:   { bg: '#d1fae5', color: '#065f46' },
  completed: { bg: '#f0fdf4', color: '#166534' },
  cancelled: { bg: '#fef2f2', color: '#b91c1c' },
};

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const EMPTY_FORM = {
  name: '', activity_type: '', date: '', end_date: '', start_time: '',
  end_time: '', location: 'Église principale', description: '', status: 'planned',
  is_recurring: false, recurrence_days: [], recurrence_end: '', recurrence_no_end: true,
  is_global: false,
};

const inp = {
  width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0',
  borderRadius: '8px', fontSize: '0.9rem', color: '#1e293b', boxSizing: 'border-box',
  outline: 'none', background: '#f8fafc',
};

export default function Activities() {
  const { user } = useAuth();
  const { activeChurchId, isPlatformAdmin } = useChurch();
  const isGlobal = useGlobalView();
  const mob = useIsMobile();
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // activity object
  const [viewMode, setViewMode] = useState('list');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showManageTypes, setShowManageTypes] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '', color: '#3B82F6', description: '' });
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [savingType, setSavingType] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState(null);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const { hasGroupAccess } = usePermissions();
  const canEdit = user?.role === 'admin' || user?.role === 'superviseur' || user?.role === 'responsable' || user?.role === 'accueil' || isPlatformAdmin || hasGroupAccess('activities');

  useEffect(() => {
    if (sessionStorage.getItem('openCreateActivity') === '1') {
      sessionStorage.removeItem('openCreateActivity');
      openCreate();
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/activities/?ordering=-date,-start_time&page_size=200';
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterType) url += `&activity_type=${filterType}`;
      if (filterDateFrom) url += `&date_from=${filterDateFrom}`;
      if (filterDateTo) url += `&date_to=${filterDateTo}`;
      if (filterSearch) url += `&search=${encodeURIComponent(filterSearch)}`;
      const res = await api.get(url);
      setActivities(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      toast.error('Erreur lors du chargement des activités.');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterDateFrom, filterDateTo, filterSearch, activeChurchId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchActivities(); }, [fetchActivities]);
  useEffect(() => { setSelectedIds([]); }, [activities]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === activities.length ? [] : activities.map(a => a.id));
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => api.delete(`/activities/${id}/`)));
      toast.success(`${selectedIds.length} activité(s) supprimée(s).`);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      fetchActivities();
    } catch {
      toast.error('Erreur lors de la suppression.');
    } finally {
      setBulkDeleting(false);
    }
  };
  useEffect(() => {
    api.get('/activities/types/').then(r =>
      setActivityTypes(Array.isArray(r.data) ? r.data : r.data.results || [])
    );
  }, [activeChurchId]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (act) => {
    setEditingId(act.id);
    setForm({
      name: act.name || '',
      activity_type: act.activity_type || '',
      date: act.date || '',
      end_date: act.end_date || '',
      start_time: act.start_time?.slice(0, 5) || '',
      end_time: act.end_time?.slice(0, 5) || '',
      location: act.location || 'Église principale',
      description: act.description || '',
      status: act.status || 'planned',
      is_recurring: act.is_recurring || false,
      recurrence_days: act.recurrence_days || [],
      recurrence_end: act.recurrence_end || '',
      recurrence_no_end: !act.recurrence_end,
      is_global: act.is_global || false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = checkRequired(form, {
      name: 'Nom', date: form.is_recurring ? 'Date de début' : 'Date', start_time: 'Heure de début',
    });
    if (form.is_recurring && form.recurrence_days.length === 0)
      errors.recurrence_days = requiredMessage('Jours de répétition');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [typeErrors, setTypeErrors] = useState({});

  const handleCreateType = async (e) => {
    e.preventDefault();
    const errors = checkRequired(typeForm, { name: 'Nom' });
    setTypeErrors(errors);
    if (Object.keys(errors).length) return;
    setSavingType(true);
    try {
      if (editingTypeId) {
        const res = await api.put(`/activities/types/${editingTypeId}/`, typeForm);
        setActivityTypes(prev => prev.map(t => t.id === editingTypeId ? res.data : t));
        toast.success(`Type "${res.data.name}" modifié !`);
      } else {
        const res = await api.post('/activities/types/', typeForm);
        setActivityTypes(prev => [...prev, res.data]);
        setForm(f => ({ ...f, activity_type: res.data.id }));
        toast.success(`Type "${res.data.name}" créé !`);
      }
      setTypeForm({ name: '', color: '#3B82F6', description: '' });
      setEditingTypeId(null);
      setShowTypeModal(false);
    } catch (err) {
      const errors = apiFieldErrors(err, { name: 'Nom' });
      setTypeErrors(errors);
      toast.error(firstErrorMessage(errors, { name: 'Nom' }, 'Erreur lors de la sauvegarde du type.'));
    } finally {
      setSavingType(false);
    }
  };

  const handleDeleteType = async (typeId) => {
    setDeletingTypeId(typeId);
    try {
      await api.delete(`/activities/types/${typeId}/`);
      setActivityTypes(prev => prev.filter(t => t.id !== typeId));
      toast.success('Type supprimé.');
    } catch {
      toast.error('Impossible de supprimer ce type (il est peut-être utilisé par des activités).');
    } finally {
      setDeletingTypeId(null);
    }
  };

  const openEditType = (t) => {
    setEditingTypeId(t.id);
    setTypeForm({ name: t.name, color: t.color, description: t.description || '' });
    setShowManageTypes(false);
    setTypeErrors({}); setShowTypeModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.activity_type) delete payload.activity_type;
      if (!payload.end_time) delete payload.end_time;
      if (!payload.end_date) payload.end_date = null;
      if (!payload.is_recurring) {
        payload.recurrence_days = [];
        payload.recurrence_end = null;
      } else {
        payload.recurrence_end = payload.recurrence_no_end ? null : (payload.recurrence_end || null);
      }
      delete payload.recurrence_no_end;
      if (!isPlatformAdmin) delete payload.is_global;  // réservé au superadmin (contrôlé côté serveur)
      if (isPlatformAdmin && activeChurchId) payload.church_id = activeChurchId;

      if (editingId) {
        await api.put(`/activities/${editingId}/`, payload);
        toast.success('Activité modifiée avec succès !');
      } else {
        await api.post('/activities/', payload);
        toast.success('Activité créée avec succès !');
      }
      setShowModal(false);
      fetchActivities();
    } catch (err) {
      const errors = apiFieldErrors(err, ACTIVITY_LABELS);
      setFormErrors(errors);
      toast.error(firstErrorMessage(errors, ACTIVITY_LABELS, 'Erreur lors de la sauvegarde.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (act) => {
    try {
      await api.delete(`/activities/${act.id}/`);
      toast.success('Activité supprimée.');
      setDeleteConfirm(null);
      fetchActivities();
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  const handleToggleStatus = async (act) => {
    const newStatus = act.status === 'cancelled' ? 'planned' : 'cancelled';
    try {
      await api.patch(`/activities/${act.id}/`, { status: newStatus });
      toast.success(newStatus === 'cancelled' ? 'Activité désactivée.' : 'Activité réactivée.');
      fetchActivities();
    } catch {
      toast.error('Erreur lors du changement de statut.');
    }
  };

  const toggleDay = (d) => {
    const days = form.recurrence_days.includes(d)
      ? form.recurrence_days.filter(x => x !== d)
      : [...form.recurrence_days, d];
    setForm({ ...form, recurrence_days: days });
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>Activités</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Toggle liste/calendrier */}
          <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
            {[['list', ListIcon, 'Liste'], ['calendar', CalendarIcon, 'Calendrier']].map(([mode, Icon, label], i) => (
              <button key={mode}
                style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, transition: 'background 0.15s', borderLeft: i > 0 ? '1px solid #d1d5db' : 'none', backgroundColor: viewMode === mode ? '#1e40af' : 'white', color: viewMode === mode ? 'white' : '#374151' }}
                onClick={() => setViewMode(mode)}>
                <Icon size={16} color={viewMode === mode ? 'white' : '#374151'} />
                <span style={{ marginLeft: '5px' }}>{label}</span>
              </button>
            ))}
          </div>
          {canEdit && (
            <button style={{ backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
              onClick={openCreate}>
              <PlusIcon size={15} color="white" /> Nouvelle activité
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      {viewMode === 'list' && (
        <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
          {/* Ligne 1 : recherche */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input style={{ width: '100%', padding: '8px 12px 8px 33px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', color: '#374151', boxSizing: 'border-box' }}
                placeholder="Rechercher une activité..." value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)} />
            </div>
            {(filterStatus || filterType || filterDateFrom || filterDateTo || filterSearch) && (
              <button style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', padding: '8px 14px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 700, whiteSpace: 'nowrap' }}
                onClick={() => { setFilterStatus(''); setFilterType(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterSearch(''); }}>
                ✕ Réinitialiser
              </button>
            )}
          </div>
          {/* Ligne 2 : selects + dates */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', color: '#374151', backgroundColor: 'white', cursor: 'pointer' }}
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', color: '#374151', backgroundColor: 'white', cursor: 'pointer' }}
              value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">Tous les types</option>
              {activityTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Du</span>
              <input type="date" style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', color: '#374151' }}
                value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Au</span>
              <input type="date" style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', color: '#374151' }}
                value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
            </div>
            {!loading && (
              <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                {activities.length} activité{activities.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Barre de sélection multiple */}
      {viewMode === 'list' && canEdit && activities.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '0 4px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox"
              checked={selectedIds.length === activities.length}
              onChange={toggleSelectAll}
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1e40af' }} />
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
              {selectedIds.length > 0 ? `${selectedIds.length} sélectionnée(s)` : 'Tout sélectionner'}
            </span>
          </label>
          {selectedIds.length > 0 && (
            <button onClick={() => setBulkDeleteConfirm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '7px', padding: '7px 14px', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 700 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              Supprimer la sélection
            </button>
          )}
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <LoadingSpinner size={36} label="Chargement..." />
        </div>
      ) : viewMode === 'calendar' ? (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0' }}>
          <ActivityCalendar
            activities={activities}
            canEdit={canEdit}
            onEdit={openEdit}
            onDelete={act => setDeleteConfirm(act)}
            onToggleStatus={handleToggleStatus}
          />
        </div>
      ) : activities.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><CalendarIcon size={48} color="#cbd5e1" /></div>
          <p>Aucune activité trouvée.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {activities.map(act => {
            const sc = STATUS_COLORS[act.status] || STATUS_COLORS.planned;
            return (
              <div key={act.id} style={{ backgroundColor: 'white', borderRadius: '10px', padding: '14px 18px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: mob ? 'wrap' : 'nowrap' }}>
                {/* Checkbox sélection */}
                {canEdit && (
                  <input type="checkbox" checked={selectedIds.includes(act.id)}
                    onChange={() => toggleSelect(act.id)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1e40af', flexShrink: 0 }} />
                )}
                {/* Point couleur type */}
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: act.activity_type_detail?.color || '#3b82f6', flexShrink: 0 }} />

                {/* Infos */}
                <Link to={`/activites/${act.id}`} style={{ flex: 1, textDecoration: 'none', minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {act.name}
                    {isGlobal && <ChurchBadge name={act.church_name} />}
                    {act.is_global && (
                      <span style={{ fontSize: '0.65rem', padding: '2px 7px', background: '#eef2ff', color: '#3730a3', borderRadius: '999px', fontWeight: 700 }}>RCC Global</span>
                    )}
                    {act.is_recurring && (
                      <span style={{ fontSize: '0.65rem', padding: '2px 7px', background: '#f0fdf4', color: '#16a34a', borderRadius: '999px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '3px' }}>
                        ↻ Récurrent
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    {act.date}{act.end_date && act.end_date !== act.date ? ` → ${act.end_date}` : ''} · {act.start_time?.slice(0, 5)}{act.end_time ? ` - ${act.end_time.slice(0, 5)}` : ''} · {act.location}
                    {act.is_recurring && act.recurrence_days?.length > 0 && (
                      <span style={{ marginLeft: '8px', color: '#16a34a' }}>
                        · {act.recurrence_days.map(d => DAYS_FR[d]).join(', ')}
                        {act.recurrence_end ? ` jusqu'au ${act.recurrence_end}` : ' · Sans fin'}
                      </span>
                    )}
                  </div>
                </Link>

                <span style={{ fontSize: '0.75rem', padding: '3px 10px', borderRadius: '999px', fontWeight: 600, backgroundColor: sc.bg, color: sc.color, flexShrink: 0 }}>
                  {STATUS_LABELS[act.status] || act.status}
                </span>
                <div style={{ textAlign: 'center', flexShrink: 0 }}>
                  <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1e40af' }}>{act.total_attendance}</div>
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>présences</div>
                </div>

                {/* Actions */}
                {canEdit && (
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button title="Modifier" onClick={() => openEdit(act)}
                      style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '7px', padding: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#1e40af' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                    <button title={act.status === 'cancelled' ? 'Réactiver' : 'Désactiver'} onClick={() => handleToggleStatus(act)}
                      style={{ background: act.status === 'cancelled' ? '#f0fdf4' : '#fef9c3', border: `1px solid ${act.status === 'cancelled' ? '#bbf7d0' : '#fde68a'}`, borderRadius: '7px', padding: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: act.status === 'cancelled' ? '#16a34a' : '#d97706' }}>
                      {act.status === 'cancelled'
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      }
                    </button>
                    <button title="Supprimer" onClick={() => setDeleteConfirm(act)}
                      style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', padding: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#dc2626' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal Créer/Modifier ── */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: mob ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000, padding: mob ? 0 : '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: mob ? '20px 20px 0 0' : '16px', padding: '28px', width: '100%', maxWidth: mob ? '100%' : '600px', maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                {editingId ? 'Modifier l\'activité' : 'Nouvelle activité'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              {/* Nom */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Nom<Req /></label>
                <input style={{ ...inp, borderColor: formErrors.name ? ERROR_COLOR : '#e2e8f0' }}
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Culte du dimanche" />
                <FieldError message={formErrors.name} />
              </div>

              {/* Type + Statut */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <label style={{ fontSize: '0.83rem', fontWeight: 600, color: '#374151' }}>Type</label>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      {activityTypes.length > 0 && (
                        <button type="button" onClick={() => setShowManageTypes(true)}
                          style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', background: '#f1f5f9', border: 'none', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer' }}>
                          Gérer
                        </button>
                      )}
                      <button type="button" onClick={() => { setEditingTypeId(null); setTypeForm({ name: '', color: '#3B82F6', description: '' }); setTypeErrors({}); setShowTypeModal(true); }}
                        style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6366f1', background: '#eef2ff', border: 'none', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer' }}>
                        + Nouveau
                      </button>
                    </div>
                  </div>
                  <select style={inp} value={form.activity_type} onChange={e => setForm({ ...form, activity_type: e.target.value })}>
                    <option value="">Aucun type</option>
                    {activityTypes.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Statut</label>
                  <select style={inp} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>

              {/* Date début + heure début */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                    {form.is_recurring ? 'Date de début' : 'Date'}<Req />
                  </label>
                  <input type="date" style={{ ...inp, borderColor: formErrors.date ? ERROR_COLOR : '#e2e8f0' }}
                    value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                  <FieldError message={formErrors.date} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Heure de début<Req /></label>
                  <input type="time" style={{ ...inp, borderColor: formErrors.start_time ? ERROR_COLOR : '#e2e8f0' }}
                    value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} />
                  <FieldError message={formErrors.start_time} />
                </div>
              </div>

              {/* Date fin + heure fin */}
              <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>
                    Date de fin <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel)</span>
                  </label>
                  <input type="date" style={inp}
                    value={form.end_date}
                    min={form.date || undefined}
                    onChange={e => setForm({ ...form, end_date: e.target.value })} />
                  {form.end_date && form.date && form.end_date < form.date && (
                    <span style={{ color: '#ef4444', fontSize: '0.78rem' }}>La date de fin doit être après la date de début.</span>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Heure de fin</label>
                  <input type="time" style={inp} value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} />
                </div>
              </div>

              {/* Lieu */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Lieu</label>
                <input style={inp} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Description</label>
                <textarea style={{ ...inp, height: '68px', resize: 'vertical' }}
                  value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Description optionnelle..." />
              </div>

              {/* ── RCC Global : réservé au super administrateur ── */}
              {isPlatformAdmin && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', border: '1.5px solid #c7d2fe', background: form.is_global ? '#eef2ff' : '#f8fafc', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' }}>
                  <input type="checkbox" checked={form.is_global} onChange={e => setForm({ ...form, is_global: e.target.checked })}
                    style={{ width: '16px', height: '16px', marginTop: '2px', accentColor: '#4f46e5' }} />
                  <span>
                    <span style={{ display: 'block', fontWeight: 700, fontSize: '0.88rem', color: '#3730a3' }}>Activité RCC Global (tous les Centres)</span>
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Visible dans l'Espace Fidèle de toutes les églises, en plus de celle qui l'organise.</span>
                  </span>
                </label>
              )}

              {/* ── Section Récurrence ── */}
              <div style={{ border: '1.5px solid #e2e8f0', borderRadius: '12px', padding: '16px', marginBottom: '20px', background: form.is_recurring ? '#f0fdf4' : '#f8fafc' }}>
                {/* Toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
                  <div
                    onClick={() => setForm({ ...form, is_recurring: !form.is_recurring })}
                    style={{ width: '44px', height: '24px', borderRadius: '999px', background: form.is_recurring ? '#16a34a' : '#cbd5e1', position: 'relative', transition: 'background 0.2s', flexShrink: 0, cursor: 'pointer' }}>
                    <div style={{ position: 'absolute', top: '3px', left: form.is_recurring ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
                  </div>
                  <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>Activité récurrente</span>
                  {form.is_recurring && <span style={{ fontSize: '0.7rem', padding: '2px 8px', background: '#dcfce7', color: '#16a34a', borderRadius: '999px', fontWeight: 700 }}>Activé</span>}
                </label>

                {form.is_recurring && (
                  <div style={{ marginTop: '16px' }}>
                    {/* Jours de la semaine */}
                    <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Jours de répétition<Req /></label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                      {DAYS_FR.map((d, i) => {
                        const active = form.recurrence_days.includes(i);
                        return (
                          <button key={i} type="button" onClick={() => toggleDay(i)}
                            style={{ padding: '6px 12px', borderRadius: '8px', border: `1.5px solid ${active ? '#16a34a' : '#d1d5db'}`, background: active ? '#dcfce7' : 'white', color: active ? '#16a34a' : '#374151', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.15s' }}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                    {form.recurrence_days.length > 0 && (
                      <div style={{ fontSize: '0.78rem', color: '#16a34a', marginBottom: '4px' }}>
                        ↻ Tous les {form.recurrence_days.map(d => DAYS_FULL[d]).join(', ')}
                      </div>
                    )}
                    <FieldError message={formErrors.recurrence_days} />

                    {/* Fin de récurrence */}
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '10px', userSelect: 'none' }}>
                        <input type="checkbox" checked={form.recurrence_no_end}
                          onChange={e => setForm({ ...form, recurrence_no_end: e.target.checked, recurrence_end: '' })}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1e40af' }} />
                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#374151' }}>Sans date de fin (récurrence infinie)</span>
                      </label>
                      {!form.recurrence_no_end && (
                        <div>
                          <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Date de fin de récurrence</label>
                          <input type="date" style={{ ...inp, maxWidth: '240px' }}
                            value={form.recurrence_end}
                            min={form.date}
                            onChange={e => setForm({ ...form, recurrence_end: e.target.value })} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)}
                  style={{ backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
                  Annuler
                </button>
                <button type="submit" disabled={saving}
                  style={{ backgroundColor: saving ? '#93c5fd' : '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: 700 }}>
                  {saving ? 'Enregistrement...' : editingId ? 'Enregistrer' : 'Créer l\'activité'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal création de type ── */}
      {showTypeModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setShowTypeModal(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>{editingTypeId ? 'Modifier le type' : 'Nouveau type d\'activité'}</h3>
            <form onSubmit={handleCreateType} noValidate>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Nom<Req /></label>
                <input style={{ ...inp, borderColor: typeErrors.name ? ERROR_COLOR : '#e2e8f0' }} placeholder="Ex: Culte, Mercredi, Jeûne..."
                  value={typeForm.name} onChange={e => setTypeForm({ ...typeForm, name: e.target.value })} />
                <FieldError message={typeErrors.name} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' }}>Description (optionnel)</label>
                <input style={inp} placeholder="Courte description..."
                  value={typeForm.description} onChange={e => setTypeForm({ ...typeForm, description: e.target.value })} />
              </div>
              <div style={{ marginBottom: '22px' }}>
                <label style={{ display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Couleur</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="color" value={typeForm.color}
                    onChange={e => setTypeForm({ ...typeForm, color: e.target.value })}
                    style={{ width: '44px', height: '44px', padding: '2px', border: '2px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: 'none' }} />
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316'].map(c => (
                      <div key={c} onClick={() => setTypeForm({ ...typeForm, color: c })}
                        style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: c, cursor: 'pointer', border: typeForm.color === c ? '3px solid #1e293b' : '2px solid transparent', transition: 'border 0.15s' }} />
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: '10px', padding: '6px 12px', background: typeForm.color + '20', borderLeft: `4px solid ${typeForm.color}`, borderRadius: '6px', fontSize: '0.83rem', fontWeight: 600, color: typeForm.color }}>
                  {typeForm.name || 'Aperçu du type'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={() => setShowTypeModal(false)}
                  style={{ flex: 1, padding: '11px', background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
                  Annuler
                </button>
                <button type="submit" disabled={savingType || !typeForm.name.trim()}
                  style={{ flex: 1, padding: '11px', background: savingType || !typeForm.name.trim() ? '#a5b4fc' : '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' }}>
                  {savingType ? 'Création...' : 'Créer le type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal gérer les types ── */}
      {showManageTypes && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setShowManageTypes(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>Gérer les types</h3>
              <button onClick={() => setShowManageTypes(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '1.2rem', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {activityTypes.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: t.color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{t.name}</div>
                    {t.description && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t.description}</div>}
                  </div>
                  <button onClick={() => openEditType(t)}
                    style={{ padding: '5px 10px', fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: 'pointer' }}>
                    Modifier
                  </button>
                  <button onClick={() => handleDeleteType(t.id)} disabled={deletingTypeId === t.id}
                    style={{ padding: '5px 10px', fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer' }}>
                    {deletingTypeId === t.id ? '...' : 'Suppr.'}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => { setEditingTypeId(null); setTypeForm({ name: '', color: '#3B82F6', description: '' }); setShowManageTypes(false); setTypeErrors({}); setShowTypeModal(true); }}
              style={{ marginTop: '16px', width: '100%', padding: '10px', backgroundColor: '#eef2ff', color: '#6366f1', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>
              + Créer un nouveau type
            </button>
          </div>
        </div>
      )}

      {/* ── Confirmation suppression multiple ── */}
      {bulkDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setBulkDeleteConfirm(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M9 6V4h6v2"/></svg>
            </div>
            <h3 style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>
              Supprimer {selectedIds.length} activité{selectedIds.length !== 1 ? 's' : ''} ?
            </h3>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem', margin: '0 0 24px' }}>
              Ces activités seront supprimées définitivement. Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleting}
                style={{ flex: 1, padding: '11px', background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
                Annuler
              </button>
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                style={{ flex: 1, padding: '11px', background: bulkDeleting ? '#fca5a5' : '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: bulkDeleting ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.9rem' }}>
                {bulkDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation suppression ── */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}
          onClick={e => { if (e.target === e.currentTarget) setDeleteConfirm(null); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M9 6V4h6v2"/></svg>
            </div>
            <h3 style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Supprimer l'activité ?</h3>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.9rem', margin: '0 0 24px' }}>
              "<strong>{deleteConfirm.name}</strong>" sera supprimée définitivement. Cette action est irréversible.
            </p>
            {deleteConfirm.is_recurring && (
              <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.83rem', color: '#92400e', textAlign: 'center' }}>
                ⚠️ Cette activité est récurrente. Toute la série sera supprimée.
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ flex: 1, padding: '11px', background: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}>
                Annuler
              </button>
              <button onClick={() => handleDelete(deleteConfirm)}
                style={{ flex: 1, padding: '11px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
